import React, { useState } from "react";
import {
  Layout,
  Card,
  Tabs,
  Button,
  Space,
  Spin,
  Row,
  Col,
  Typography,
  Alert,
  Divider,
  Upload,
  message,
} from "antd";

import {
  ReloadOutlined,
  FullscreenOutlined,
  UploadOutlined,
  DownloadOutlined,
} from "@ant-design/icons";

import DataTable from "./DataTable";
import InitialGraphVisualization from "./InitialGraphVisualization";
import NettedGraphVisualization from "./NettedGraphVisualization";
import GraphStatesVisualization from "./GraphStatesVisualization";
import CentralClearingVisualization from "./CentralClearingVisualization";
import DefaultSimulation from "./DefaultSimulation";
import CodeDownload from "./CodeDownload";
import "./App.css";

const { Header, Content, Footer } = Layout;
const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;

function App() {
  const [updateTrigger, setUpdateTrigger] = useState(0);
  const [loading, setLoading] = useState(false);
  const [simRunning] = useState(false);
  const [activeTab, setActiveTab] = useState("info");
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const handleTableDataChange = async (newData) => {
    try {
      setLoading(true);
      const response = await fetch(
        "https://api.repo.tradingmechanism.com/update_and_visualize",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(newData),
        }
      );

      if (response.ok) {
        // Trigger a re-render of all visualizations
        setUpdateTrigger((prev) => prev + 1);
      }
    } catch (error) {
      console.error("Error updating visualizations:", error);
    } finally {
      setLoading(false);
    }
  };
  const handleRefresh = async () => {
    setLoading(true);
    try {
      // Call the init-simulation endpoint to reset data
      const response = await fetch("https://api.repo.tradingmechanism.com/api/init-simulation");
      if (response.ok) {
        // Increment refreshTrigger to cause DataTable to reload
        setRefreshTrigger((prev) => prev + 1);
        // Also update visualization trigger if needed
        setUpdateTrigger((prev) => prev + 1);
      }
    } catch (error) {
      console.error("Error refreshing data:", error);
    } finally {
      setLoading(false);
    }
  };
  const handleDownloadSample = () => {
    fetch("https://api.repo.tradingmechanism.com/api/download-sample")
      .then((response) => {
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        return response.blob();
      })
      .then((blob) => {
        // Create a URL for the blob
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.style.display = "none";
        a.href = url;
        a.download = "sample_data.csv";
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        message.success("Sample data downloaded successfully");
      })
      .catch((error) => {
        console.error("Error downloading sample data:", error);
        message.error("Failed to download sample data");
      });
  };

  // Configure file upload
  const uploadProps = {
    name: "file",
    action: "https://api.repo.tradingmechanism.com/api/upload-data",
    accept: ".csv",
    showUploadList: false,
    beforeUpload: (file) => {
      const isCsv = file.type === "text/csv" || file.name.endsWith(".csv");
      if (!isCsv) {
        message.error("You can only upload CSV files!");
        return Upload.LIST_IGNORE;
      }

      // Read and validate the CSV file content
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsText(file);
        reader.onload = (e) => {
          const content = e.target.result;
          const validation = validateCsvContent(content);

          if (validation.valid) {
            resolve(true);
          } else {
            message.error(validation.message);
            reject(false);
          }
        };
        reader.onerror = () => {
          message.error("Failed to read file");
          reject(false);
        };
      });
    },
    onChange: (info) => {
      if (info.file.status === "uploading") {
        setLoading(true);
        return;
      }

      if (info.file.status === "done") {
        setLoading(false);
        if (info.file.response && info.file.response.status === "success") {
          message.success("File uploaded successfully");
          // Trigger data refresh and visualization update
          setRefreshTrigger((prev) => prev + 1);
          setUpdateTrigger((prev) => prev + 1);
        } else {
          message.error(
            info.file.response?.message ||
              "Upload failed. Please use the template format without changing any columns."
          );
        }
      } else if (info.file.status === "error") {
        setLoading(false);
        message.error("Upload failed");
      }
    },
  };

  // Validation function for CSV content
  const validateCsvContent = (content) => {
    // Parse CSV content
    const lines = content.split("\n").filter((line) => line.trim() !== "");

    // Check if header exists
    if (lines.length < 2) {
      return {
        valid: false,
        message: "CSV file must include a header row and at least one data row",
      };
    }

    // Get headers
    const headers = lines[0].split(",").map((h) => h.trim());

    // Check required columns exist
    const requiredColumns = [
      "firm_identifier",
      "counterparty_identifier",
      "first_leg_price",
      "second_leg_price",
      "collateral_units",
    ];

    for (const col of requiredColumns) {
      if (!headers.includes(col)) {
        return { valid: false, message: `Required column '${col}' is missing` };
      }
    }

    // Get column indices for required fields
    const colIndices = {};
    requiredColumns.forEach((col) => {
      colIndices[col] = headers.indexOf(col);
    });

    // Check row count (excluding header)
    const dataRows = lines.slice(1);
    if (dataRows.length > 20) {
      return {
        valid: false,
        message: "CSV file cannot contain more than 20 data rows",
      };
    }

    // Check each row for required values and data types
    for (let i = 0; i < dataRows.length; i++) {
      const rowNum = i + 2; // Actual row number in spreadsheet (header is row 1)
      const row = dataRows[i].split(",").map((cell) => cell.trim());

      // Check if row has enough columns
      if (row.length < headers.length) {
        return {
          valid: false,
          message: `Row ${rowNum} doesn't have enough columns`,
        };
      }

      // Check required values
      for (const col of requiredColumns) {
        const value = row[colIndices[col]];
        if (!value || value === "") {
          return {
            valid: false,
            message: `Row ${rowNum} is missing required value for '${col}'`,
          };
        }

        // Check data types
        if (col === "firm_identifier" || col === "counterparty_identifier") {
          // String validation is implicit - all CSV values are strings
        } else {
          // For numeric fields
          const numValue = parseFloat(value);
          if (isNaN(numValue)) {
            return {
              valid: false,
              message: `Row ${rowNum}: '${col}' must be a number`,
            };
          }
        }
      }
    }

    return { valid: true };
  };
  return (
    <Layout className="app-layout">
      <Header className="app-header">
        <div className="logo" />
        <Title
          level={3}
          style={{
            color: "#fff",
            margin: 0,
            paddingBottom: "4px",
            paddingLeft: "0",
            maxWidth: "calc(100% - 80px)",
          }}
        >
          A Trading Mechanism to Increase Intermediation Capacity in the Repo
          Market
        </Title>
      </Header>

      <Content className="app-content">
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          type="card"
          className="main-tabs"
        >
          {/* New Info tab added as first tab */}
          <TabPane tab="Information" key="info">
            <Card className="info-card">
              <div className="paper-info">
                <div className="authors">
                  <Title level={4}>Authors and Developers</Title>
                  <Paragraph>
                    <strong><a href="https://leanntai.vercel.app/">LeAnn Tai</a></strong>, MIT
                    <br />
                    <strong><a href="https://danieljaronoff.io/">Daniel Aronoff</a></strong>, MIT
                    <br />
                    <strong><a href="https://madars.org/">Madars Virza</a></strong>, MIT
                    <br />
                    <strong><a href="https://economics.mit.edu/people/faculty/robert-m-townsend">Robert M. Townsend</a></strong>, MIT
                    <br />
                  </Paragraph>
                </div>
                <Divider />
                <div className="abstract">
                  <Title level={4}>Abstract</Title>
                  <Paragraph>
                    This paper examines the role of intermediation in repo
                    markets and its implications for financial stability. We
                    develop a network model of the interdealer repo market and
                    analyze how different trading mechanisms affect market
                    efficiency, liquidity provision, and systemic risk. Our
                    simulation demonstrates that central clearing can reduce
                    counterparty exposure but may concentrate risk, while
                    bilateral netting offers flexibility but potentially creates
                    complex interdependencies. The model provides insights into
                    optimal market design and regulatory approaches for repo
                    markets.
                  </Paragraph>
                </div>
                <Divider />
                <div className="keywords">
                  <Title level={4}>Keywords</Title>
                  <Paragraph>
                    Microeconomic Engineering, Incentive Compatible Mechanisms,
                    Market Microstructure, Financial Intermediaries, Shadow
                    Banking, Broker-Dealer, Bank Regulation, Capital
                    Requirements
                  </Paragraph>
                </div>
                <Divider />
                <div className="instructions">
                  <Title level={4}>Simulation Instructions</Title>
                  <Paragraph>
                    This interactive simulation allows you to explore different
                    trading mechanisms in repo markets. Navigate to the
                    "Visualization" tab to view network representations of the
                    market under different clearing arrangements. The "Default
                    Simulation" tab provides pre-configured scenarios based on
                    empirical data from major repo markets. For source code and
                    package installation instructoins, take a look at the "Code
                    and Download" tab.
                  </Paragraph>
                </div>
              </div>
            </Card>
          </TabPane>

          <TabPane tab="Visualization" key="visualization">
            {/* New separate card for instructions and controls */}
            <Card>
              {/* Description text explaining how to use the buttons */}
              <div>
                <p>
                  Use these controls to manage your simulation data. Download
                  the sample dataset to get started, modify the entries, and
                  upload the new file for custom simulations. Note that the
                  columns must remain exactly the same and all entries must
                  follow the sample data's data type (i.e. numbers or letters).
                  Only corresponding entries of the columns in the table below
                  are required, the rest can be left blank. Press the reset
                  button to restart the visualization and simulation with the
                  original default data.
                </p>
              </div>

              {/* Alert for data confidentiality */}
              <Alert
                className="data-alert"
                message="Important: Data Security Notice"
                description="Please do not upload any confidential or sensitive data. All uploaded data is used only for simulation purposes and may not be saved when the site is reloaded."
                type="warning"
                showIcon
              />

              {/* Buttons with text labels */}
              <div className="control-buttons-container">
                <Space size="large">
                  <Button
                    icon={<DownloadOutlined />}
                    onClick={handleDownloadSample}
                  >
                    Download Sample Data
                  </Button>

                  <Upload {...uploadProps}>
                    <Button icon={<UploadOutlined />}>Upload Your Data</Button>
                  </Upload>

                  <Button
                    type="secondary"
                    icon={<ReloadOutlined />}
                    onClick={handleRefresh}
                    loading={loading}
                  >
                    Reset Simulation
                  </Button>
                </Space>
              </div>
            </Card>

            {/* Separate card for the data table */}
            <Card
              className="visualizations-container"
              title={<Space>Data</Space>}
            >
              {/* Pass refreshTrigger to DataTable */}
              <DataTable
                onDataChange={handleTableDataChange}
                refreshTrigger={refreshTrigger}
              />

              {simRunning && (
                <div className="sim-progress">
                  <Text>Simulation in progress</Text>
                </div>
              )}
            </Card>

            {/* Visualizations container */}
            <div className="visualizations-container">
              <Spin spinning={loading} tip="Updating visualizations...">
                <Row gutter={[24, 24]}>
                  <Col xs={24} lg={12}>
                    <Card
                      title="Initial Graph"
                      className="viz-card"
                      extra={<FullscreenOutlined />}
                    >
                      <InitialGraphVisualization
                        key={`initial-${updateTrigger}`}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} lg={12}>
                    <Card
                      title="Netted Graph"
                      className="viz-card"
                      extra={<FullscreenOutlined />}
                    >
                      <NettedGraphVisualization
                        key={`netted-${updateTrigger}`}
                      />
                    </Card>
                  </Col>
                  <Col xs={24}>
                    <Card
                      title="Chain and Cycle Decomposition"
                      className="viz-card"
                      extra={<FullscreenOutlined />}
                    >
                      <GraphStatesVisualization
                        key={`states-${updateTrigger}`}
                      />
                    </Card>
                  </Col>
                  <Col xs={24}>
                    <Card
                      title="Central Clearing"
                      className="viz-card"
                      extra={<FullscreenOutlined />}
                    >
                      <CentralClearingVisualization
                        key={`clearing-${updateTrigger}`}
                      />
                    </Card>
                  </Col>
                </Row>
              </Spin>
            </div>
          </TabPane>
          <TabPane tab="Default Simulation" key="defaultSim">
            <Card className="default-sim-card">
              <DefaultSimulation />
            </Card>
          </TabPane>
          <TabPane tab="Code and Download" key="code">
            <Card className="default-sim-card">
              {" "}
              <CodeDownload />{" "}
            </Card>
          </TabPane>
        </Tabs>
      </Content>

      <Footer className="app-footer">
        <Text type="secondary">
          A Trading Mechanism to Increase Intermediation Capacity in the Repo
          Market © 2025
        </Text>
      </Footer>
    </Layout>
  );
}

export default App;
